import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import PropTypes from "prop-types";
import React, { forwardRef } from "react";
import { NOOP } from "~/constants/callbacks";

/**
 * A reusable search input component with a search icon and a clear button, styled for Bootstrap 4.6.
 * This version uses absolute positioning to place the icons inside the input field.
 * @param {object} props - The component props.
 * @param {string} props.value - The input's current value.
 * @param {function} props.onChange - Function to call when the input value changes.
 * @param {function} props.onClear - Function to call when the clear button is clicked.
 * @param {string} props.placeholder - The placeholder text for the input.
 * @param {boolean} props.disabled - Whether the input is disabled.
 * @param {boolean} props.hasError - Whether to display the input in an error state.
 */
const SearchInput = forwardRef(
  (
    {
      value,
      onChange,
      onClear,
      placeholder = "Search...",
      autoComplete = "off",
      disabled = false,
      hasError = false,
      isLoading = false,
      onFocus = NOOP,
      onBlur = NOOP,
      onKeyDown = NOOP,
    },
    ref,
  ) => {
    const inputBgColor = disabled ? "#e9ecef" : "#fff";

    return (
      <div className="position-relative d-flex align-items-center">
        <FontAwesomeIcon
          icon="search"
          className="position-absolute text-muted"
          style={{ left: "12px", zIndex: 3, pointerEvents: "none" }}
        />

        <input
          ref={ref}
          id="search-input"
          type="text"
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          disabled={disabled}
          className={classNames("form-control", { "is-invalid": hasError })}
          style={{ paddingLeft: "38px", paddingRight: value ? "25px" : 0 }}
          required
          onFocus={onFocus}
          onBlur={onBlur}
          onKeyDown={onKeyDown}
          autoComplete={autoComplete}
        />

        {isLoading && (
          <div
            className="position-absolute d-flex align-items-center justify-content-center"
            style={{
              right: "1px",
              top: "1px",
              bottom: "1px",
              width: "25px",
              zIndex: 3,
            }}
          >
            <FontAwesomeIcon icon="spinner" pulse />
          </div>
        )}

        {!isLoading && value && onClear && (
          <div
            className="position-absolute d-flex align-items-center justify-content-center"
            style={{
              right: "1px",
              top: "1px",
              bottom: "1px",
              width: "25px",
              zIndex: 3,
              background: inputBgColor,
              borderTopRightRadius: ".25rem",
              borderBottomRightRadius: ".25rem",
            }}
          >
            <button
              type="button"
              onClick={onClear}
              className="btn btn-link text-muted shadow-none p-0"
              aria-label="Clear input"
              disabled={disabled}
            >
              <FontAwesomeIcon icon="times" />
            </button>
          </div>
        )}
      </div>
    );
  },
);

SearchInput.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  hasError: PropTypes.bool,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  onKeyDown: PropTypes.func,
  autoComplete: PropTypes.string,
  isLoading: PropTypes.bool,
};

export default SearchInput;
